import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Shared presentational pieces (plan revision R15).
 *
 * Two rules that shape everything here:
 *
 *   Not one hardcoded color. This file used to hold ten, and they were exactly
 *   what a dark mode breaks -- `ErrorNote` and `Skeleton` were built out of
 *   fixed reds and stones, so they would have stayed light on a near-black
 *   ground. Every color now comes from a token.
 *
 *   Weight and space instead of boxes. The old version put a border and a
 *   shadow around everything, which reads as a form rather than a list. A
 *   raised surface, a sharp accent rule, and a large display face do the same
 *   separating work with less furniture.
 */

export function Screen({ title, subtitle, action, children }: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto min-h-dvh max-w-3xl pb-28">
      <header className="px-5 pt-7 pb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <img src="/icons/sunflower-logo.webp" alt="Dinner App sunflower logo" width={40} height={40} className="h-10 w-10 shrink-0 rounded-xl" />
            <div className="min-w-0">
            {/*
              Uppercase monospace, tight. This is the receipt-tape reference the
              type pairing is built on -- the app reads handwritten lists, and
              the headers look like the tape they come off.
            */}
            <h1 className="font-display text-[26px] uppercase leading-none text-text-primary">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 text-[13px] leading-snug text-text-muted">{subtitle}</p>
            )}
            </div>
          </div>
          {action}
        </div>
        {/* A short accent rule instead of a full-width divider: it marks the
            start of the screen without drawing a box around it. */}
        <div className="mt-4 h-[3px] w-10 rounded-full bg-accent-vibrant" aria-hidden />
      </header>
      <main className="px-5">{children}</main>
    </div>
  );
}

/**
 * Aisle color. In a shop you scan for the section you are standing in, and a
 * color finds it faster than reading every heading -- this is the one place
 * color is load-bearing rather than decorative, which is why each hue is
 * retuned per theme rather than dimmed.
 */
const AISLE_HUES: [RegExp, string][] = [
  [/produce|fruit|veg/i, 'var(--aisle-produce)'],
  [/meat|seafood|fish|butcher/i, 'var(--aisle-meat)'],
  [/deli/i, 'var(--aisle-deli)'],
  [/dairy|egg|cheese/i, 'var(--aisle-dairy)'],
  [/baker|bread/i, 'var(--aisle-bakery)'],
  [/frozen/i, 'var(--aisle-frozen)'],
  [/canned|jarred/i, 'var(--aisle-canned)'],
  [/dry goods|pasta|grain/i, 'var(--aisle-dry)'],
  [/baking/i, 'var(--aisle-baking)'],
  [/spice|season/i, 'var(--aisle-spice)'],
  [/condiment|sauce|oil/i, 'var(--aisle-condiments)'],
];

export function aisleHue(name: string | null | undefined): string {
  if (!name) return 'var(--aisle-other)';
  for (const [re, hue] of AISLE_HUES) if (re.test(name)) return hue;
  return 'var(--aisle-other)';
}

export function Card({ children, className = '', onClick }: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  // Raised surface, no border. On both grounds the surface reads as separate
  // without an outline, and losing the outline is most of what made the old
  // screens feel like a settings page.
  const base = `rounded-card bg-bg-raised ${className}`;
  if (!onClick) return <div className={base}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} w-full text-left transition-colors active:bg-bg-inset`}
    >
      {children}
    </button>
  );
}

export function Button({ children, onClick, variant = 'primary', type = 'button', disabled, className = '' }: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
}) {
  const styles = {
    // accent-contrast rather than white: on the dark theme the accent is bright
    // enough that white-on-it fails, and near-black on it passes at 7.32:1.
    primary: 'bg-accent-vibrant text-accent-contrast active:bg-accent-deep',
    secondary: 'bg-bg-raised text-text-primary border border-border-interactive active:bg-bg-inset',
    ghost: 'text-text-muted active:text-text-primary',
    danger: 'text-danger-fg active:bg-danger-bg',
  }[variant];

  /**
   * A disabled button gets its own flat treatment rather than a faded accent.
   * Dimming the ember to 45% produced a muddy brown that read as a broken
   * button rather than an inactive one -- and "looks broken" is worse than
   * "looks off", because it makes people tap it repeatedly.
   */
  const look = disabled ? 'bg-bg-inset text-text-muted' : styles;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`tap inline-flex items-center justify-center gap-2 rounded-lg px-4 text-[13px]
                  font-semibold tracking-wide transition-colors ${look} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="font-display text-[11px] uppercase tracking-wider text-text-secondary">
        {label}
      </span>
      {hint && <span className="mt-0.5 block text-xs text-text-muted">{hint}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}

/**
 * `border-interactive` rather than `border-subtle`: the outline is the only
 * thing identifying an input, so WCAG 1.4.11 requires 3:1 against the ground.
 * The decorative token is 1.70:1 and would have been an invisible field.
 */
export const inputClass =
  'w-full rounded-lg border border-border-interactive bg-bg-raised px-3.5 py-3 text-base ' +
  'text-text-primary placeholder:text-text-muted focus:border-accent-vibrant focus:outline-none';

export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-border-subtle px-5 py-12 text-center">
      <p className="text-sm text-text-muted">{children}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <ul className="space-y-2.5" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="h-[68px] animate-pulse rounded-card bg-bg-raised" />
      ))}
    </ul>
  );
}

export function ErrorNote({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  return (
    <div
      className="mb-4 rounded-card border-l-[3px] border-danger-fg bg-danger-bg px-4 py-3"
      role="alert"
    >
      <p className="text-sm leading-snug text-danger-fg">{children}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="tap mt-1 font-display text-[11px] uppercase tracking-wider text-danger-fg underline"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function Sheet({ title, onClose, children, wide = false }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = dialog.current!;
    const opener = document.activeElement as HTMLElement | null;
    el.showModal();
    el.querySelector<HTMLElement>('[autofocus]')?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const targets = [...el.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])')].filter(target => target.getClientRects().length > 0);
      const first = targets[0], last = targets.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    el.addEventListener('keydown', trap);
    return () => { el.removeEventListener('keydown', trap); el.close(); document.body.style.overflow = overflow; opener?.focus(); };
  }, []);
  return (
    <dialog
      ref={dialog}
      aria-label={title}
      className="fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none bg-transparent p-0 text-text-primary open:flex open:flex-col open:justify-end md:open:items-center md:open:justify-center"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div
        className={`max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-bg-base pb-[env(safe-area-inset-bottom)] md:rounded-2xl ${wide ? 'md:max-w-6xl' : 'md:max-w-xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border-subtle bg-bg-base px-5 py-3.5">
          <h2 className="min-w-0 truncate font-display text-[15px] uppercase tracking-tight text-text-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="tap -mr-2 shrink-0 px-2 font-display text-[11px] uppercase tracking-wider text-text-muted"
          >
            Close
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </dialog>
  );
}

/** Relative day label. Honest about staleness rather than falsely precise. */
export function agoLabel(days: number | null, served: string | null): string {
  if (served === null || days === null) return 'Never cooked';
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `Over ${years} year${years === 1 ? '' : 's'} ago`;
}

export function formatQuantity(q: number | null, unit: string | null): string {
  if (q === null) return unit ?? '';
  // Render thirds and quarters as fractions; a list saying "0.33 cup" is worse
  // than one saying "1/3 cup".
  const fractions: [number, string][] = [
    [0.125, '⅛'], [0.25, '¼'], [1 / 3, '⅓'], [0.375, '⅜'], [0.5, '½'],
    [0.625, '⅝'], [2 / 3, '⅔'], [0.75, '¾'], [0.875, '⅞'],
  ];
  const whole = Math.floor(q);
  const frac = q - whole;
  let text: string;
  const hit = fractions.find(([v]) => Math.abs(frac - v) < 0.02);
  if (hit) text = whole > 0 ? `${whole}${hit[1]}` : hit[1];
  else text = Number.isInteger(q) ? String(q) : String(Math.round(q * 100) / 100);
  return unit ? `${text} ${unit}` : text;
}
